import {
  mapDashboardList,
  mapQueryDashboardById,
  mapQueryEditDashboardById,
  mutationDeleteDashboard,
  mutationSaveUserDashboardPreferences,
  queryCreateDashboard,
  queryDashboardById,
  queryDashboardList,
  queryUpdateDashboard,
  queryDashboardListByDatasourceId,
  mapQueryDashboardListByDatasourceId,
  queryCompanyWideActivityWidget,
  mapQueryCompanyWideActivityWidget,
  IdentityActiveSummary,
  queryDashboardByType,
  mapQueryDashboardByType
} from './queries'
import { DashboardData } from '../renderer/types/interfaces'
import graphqlService from '../../../services/graphqlService'
import { ActivityEventType } from '../../accessControl/accessControlSlice'
import {
  ActivityLogGroupingInterval,
  DATA_SOURCE_TYPES,
  FILTER_TIME_PERIOD,
  TimePeriodOptions
} from '../../../constants'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { IntlShape } from 'react-intl'

export enum DashboardCategory {
  main_dashboard = 'main_dashboard', //sytem dashboards
  datasource_summary_dashboard = 'datasource_summary_dashboard',
  custom_dashboard = 'custom_dashboard',
  privacy_ops_dashboard = 'privacy_ops_dashboard',
  compliance_pci_dashboard = 'compliance_pci_dashboard',
  compliance_hipaa_dashboard = 'compliance_hipaa_dashboard'
}

export const DashboardTypeOrder: Record<DashboardCategory, number> = {
  [DashboardCategory.main_dashboard]: 1,
  [DashboardCategory.custom_dashboard]: 2,
  [DashboardCategory.datasource_summary_dashboard]: 3,
  [DashboardCategory.privacy_ops_dashboard]: 4,
  [DashboardCategory.compliance_pci_dashboard]: 5,
  [DashboardCategory.compliance_hipaa_dashboard]: 6
}

export enum DashboardCompliances {
  PCI = 'PCI',
  HIPAA = 'HIPAA',
  GDPR = 'GDPR',
  SOC2 = 'SOC2',
  ISO = 'ISO',
  GLBA = 'GLBA',
  DPDPA = 'DPDPA',
  PDPL = 'PDPL',
  NIST = 'NIST',
  CIS8 = 'CIS8'
}

export const DashboardComplianceValues = [
  DashboardCompliances.PCI,
  DashboardCompliances.HIPAA,
  DashboardCompliances.GDPR,
  DashboardCompliances.SOC2,
  DashboardCompliances.ISO,
  DashboardCompliances.GLBA,
  DashboardCompliances.DPDPA,
  DashboardCompliances.PDPL,
  DashboardCompliances.NIST,
  DashboardCompliances.CIS8
]

export interface DashboardFilterProps {
  [FILTER_TIME_PERIOD]?: string
}

export interface CustomDashboardState {
  selectedMainDashboard?: string
  dashboardlist: DashboardData[]
  totalDashboards: number
  selectedDsSummaryDashboard?: DashboardData
  selectedDashboardData?: DashboardData
  privacyOpsDashboardData?: DashboardData
  activeCreateEditDashboardData: Omit<DashboardData, 'id'>
  activeCustomDashboardData?: Omit<DashboardData, 'id'>
  selectedWidgetForPreview?: string
  datasourceTypeForPreviewWidget?: string
  companyWideActivityWidgetData?: IdentityActiveSummary[]

  filterProps: DashboardFilterProps
}

// Fetch Dashboard List
export type DashboardListParams = {
  page: number
  pageSize: number
  sortBy?: string
  sortOrder?: number
  searchQuery?: string
  owner?: string[]
  compliance?: string[]
}

export const ACTION_FETCH_DASHBOARD_LIST = 'customDashboard/fetchDashboardList'

export const fetchDashboardList = createAsyncThunk(
  ACTION_FETCH_DASHBOARD_LIST,
  async (params?: DashboardListParams) => {
    const result = await graphqlService.execute(queryDashboardList(params))
    return mapDashboardList(result)
  }
)

// Fetch Dashboard data by datasource id
export const ACTION_FETCH_DASHBOARD_DATA_BY_DATASOURCE_ID =
  'customDashboard/fetchDashboardDataByDatasourceId'

export const fetchDashboardDataByDatasourceId = createAsyncThunk(
  ACTION_FETCH_DASHBOARD_DATA_BY_DATASOURCE_ID,
  async ({ dataSourceId, datasourceType }: { dataSourceId: string; datasourceType: string }) => {
    const result = await graphqlService.execute(
      queryDashboardListByDatasourceId(dataSourceId, datasourceType)
    )
    return mapQueryDashboardListByDatasourceId(result)
  }
)

// Update Dashboard Preferences
export const ACTION_UPDATE_DASHBOARD_PREFERENCES = 'customDashboard/updateDashboardPreferences'

export const updateDashboardPreferences = createAsyncThunk(
  ACTION_UPDATE_DASHBOARD_PREFERENCES,
  async (dashboardList: DashboardData[]) => {
    await graphqlService.execute(mutationSaveUserDashboardPreferences(dashboardList))
  }
)

export const ACTION_FETCH_DASHBOARD_BY_ID = 'customDashboard/fetchDashboardById'

export const fetchDashboardById = createAsyncThunk(
  ACTION_FETCH_DASHBOARD_BY_ID,
  async (dashboardId: string) => {
    const result = await graphqlService.execute(queryDashboardById({ dashboardId }))
    return mapQueryDashboardById(result)
  }
)

export const ACTION_FETCH_PRIVACY_OPS_DASHBOARD = 'customDashboard/privacyOpsDashboard'

export const fetchPrivacyOpsDashboard = createAsyncThunk(
  ACTION_FETCH_PRIVACY_OPS_DASHBOARD,
  async (dashboardType: string) => {
    const result = await graphqlService.execute(queryDashboardByType(dashboardType))
    return mapQueryDashboardByType(result)
  }
)

export const ACTION_FETCH_EDIT_DASHBOARD_BY_ID = 'customDashboard/fetchEditDashboardById'

export const fetchEditDashboardById = createAsyncThunk(
  ACTION_FETCH_EDIT_DASHBOARD_BY_ID,
  async ({
    dashboardId,
    intl,
    dataSourceId,
    datasourceType
  }: {
    dashboardId: string
    intl: IntlShape
    dataSourceId?: string
    datasourceType?: DATA_SOURCE_TYPES
  }) => {
    const result = await graphqlService.execute(
      queryDashboardById({ dashboardId, dataSourceId, datasourceType })
    )
    return mapQueryEditDashboardById(result, intl)
  }
)

export const ACTION_UPDATE_DASHBOARD = 'customDashboard/updateDashboard'

export const updateDashboard = createAsyncThunk(
  ACTION_UPDATE_DASHBOARD,
  async (dashboard: DashboardData, { rejectWithValue }) => {
    try {
      const result = await graphqlService.execute(queryUpdateDashboard(dashboard))
      return result
    } catch (error: any) {
      console.error(error)
      return rejectWithValue({ statusMessage: error?.errors[0]?.message || '' })
    }
  }
)

export const ACTION_CREATE_DASHBOARD = 'customDashboard/createDashboard'

export const createDashboard = createAsyncThunk(
  ACTION_CREATE_DASHBOARD,
  async (dashboard: DashboardData, { rejectWithValue }) => {
    try {
      const result = await graphqlService.execute(queryCreateDashboard(dashboard))
      if (result?.createDashboard?.dashboardId) {
        await graphqlService.execute(
          mutationSaveUserDashboardPreferences([
            {
              id: result?.createDashboard?.dashboardId,
              isDefault: false,
              isPinned: true
            } as DashboardData
          ])
        )
      }
      return result
    } catch (error: any) {
      console.error(error)
      return rejectWithValue({ statusMessage: error?.errors[0]?.message || '' })
    }
  }
)

export const ACTION_DELETE_DASHBOARD = 'customDashboard/deleteDashboard'

export const deleteDashboard = createAsyncThunk(
  ACTION_DELETE_DASHBOARD,
  async (dashboardId?: string) => {
    await graphqlService.execute(mutationDeleteDashboard(dashboardId))
  }
)

type TimeRange = {
  start: string
  end: string
}
export type GroupingInterval = { days: number } | { weeks: number } | { hours: number }
export interface CompanyWideActivityWidgetParams {
  datasourceId: string
  actualTimeRange: TimeRange
  predictedTimeRange?: TimeRange
  groupingInterval: ActivityLogGroupingInterval
  timezone: string
  features: ActivityEventType[]
  isSensitive: boolean
}
export const ACTION_FETCH_COMPANY_WIDE_ACTIVITY_WIDGET = 'fetch/companyWideActivityWidget'
export const fetchCompanyWideActivityWidget = createAsyncThunk(
  ACTION_FETCH_COMPANY_WIDE_ACTIVITY_WIDGET,
  async (params: CompanyWideActivityWidgetParams) => {
    const result = await graphqlService.execute(queryCompanyWideActivityWidget(params))
    return mapQueryCompanyWideActivityWidget(result)
  }
)

export const DEFAULT_CUSTOM_DASHBOARD_DATA = {
  id: '',
  name: 'Custom Dashboard',
  type: DashboardCategory.custom_dashboard,
  screenWidthConfig: [],
  widgets: []
}

export const initialState: CustomDashboardState = {
  dashboardlist: [],
  totalDashboards: 0,
  activeCreateEditDashboardData: {
    name: 'Custom Dashboard',
    type: DashboardCategory.custom_dashboard,
    screenWidthConfig: [],
    widgets: []
  },
  companyWideActivityWidgetData: [],
  filterProps: {
    [FILTER_TIME_PERIOD]: TimePeriodOptions.FROM_THE_START
  }
}

const customDashboardSlice = createSlice({
  name: 'customDashboards',
  initialState,
  reducers: {
    setSelectedMainDashboard: (state, action) => {
      state.selectedMainDashboard = action.payload
    },
    resetSelectedMainDashboard: (state) => {
      state.selectedMainDashboard = initialState.selectedMainDashboard
    },
    setUpdatedDashboardList: (state, action) => {
      state.dashboardlist = action.payload
    },
    setActiveCreatEditDashboardDataName: (state, action) => {
      if (state?.activeCreateEditDashboardData) {
        state.activeCreateEditDashboardData.name = action.payload
      }
    },
    setActiveCreatEditDashboardDataCompliance: (state, action) => {
      if (state?.activeCreateEditDashboardData) {
        state.activeCreateEditDashboardData.compliance = action.payload
      }
    },
    setActiveCreateEditDashboardDataWidgets: (state, action) => {
      if (state?.activeCreateEditDashboardData) {
        state.activeCreateEditDashboardData.widgets = action.payload
      }
    },
    resetActiveCreateEditDashboardData: (state) => {
      state.activeCreateEditDashboardData = initialState.activeCreateEditDashboardData
    },
    setSelectedWidgetForPreview: (state, action) => {
      state.selectedWidgetForPreview = action.payload
    },
    setDatasourceTypeForPreviewWidget: (state, action) => {
      state.datasourceTypeForPreviewWidget = action.payload
    },
    resetCompanyWideActivityWidget: (state) => {
      state.companyWideActivityWidgetData = initialState.companyWideActivityWidgetData
    },

    setFilterProps: (state, action) => {
      state.filterProps = { ...state.filterProps, ...action.payload }
    },
    resetFilterProps: (state) => {
      state.filterProps = initialState.filterProps
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDashboardList.fulfilled, (state, { payload }) => {
      state.dashboardlist = payload.list
      state.totalDashboards = payload.total
    })
    builder.addCase(fetchDashboardDataByDatasourceId.fulfilled, (state, { payload }) => {
      state.selectedDsSummaryDashboard = payload.dashboardList[0]
    })
    builder.addCase(fetchDashboardById.fulfilled, (state, { payload }) => {
      state.selectedDashboardData = payload
    })
    builder.addCase(fetchPrivacyOpsDashboard.fulfilled, (state, { payload }) => {
      state.privacyOpsDashboardData = payload
    })
    builder.addCase(fetchEditDashboardById.fulfilled, (state, { payload }) => {
      if (!payload) return
      state.activeCreateEditDashboardData = payload
    })
    builder.addCase(fetchCompanyWideActivityWidget.fulfilled, (state, { payload }) => {
      state.companyWideActivityWidgetData = payload.list
    })
  }
})

export const {
  setSelectedMainDashboard,
  setUpdatedDashboardList,
  setActiveCreatEditDashboardDataName,
  setActiveCreatEditDashboardDataCompliance,
  setActiveCreateEditDashboardDataWidgets,
  resetActiveCreateEditDashboardData,
  setSelectedWidgetForPreview,
  setDatasourceTypeForPreviewWidget,
  resetSelectedMainDashboard,
  resetCompanyWideActivityWidget,
  setFilterProps,
  resetFilterProps
} = customDashboardSlice.actions

export default customDashboardSlice.reducer
